import type { ConfiguratorCsticTarget } from "~/features/configurator/common/api/configuratorCommands";
import {
  CSTIC_AL_NAME,
  CSTIC_AR_NAME,
  CSTIC_ENABLE_ALAR_NAME,
  CSTIC_LENGTH_NAME,
  CSTIC_PART_AMOUNT_NAME,
  CSTIC_PART_SIZING_TYPE_NAME,
  CSTIC_VHOE,
  CSTIC_VSKITS_NAME,
  GET_GUIDEWAY_PIECE_SIZE_CSTIC_NAME,
  MAX_PIECE_AMOUNT_IN_KB,
} from "~/features/configurator/Linear/constants";
import { KmatType } from "~/features/configurator/Linear/kmatType";
import type { Kmat } from "~/features/configurator/Linear/models";
import { LOCATING_FACE_CODE_KEY } from "~/features/configurator/Rail/v2/configuration/classic/railConfiguratorConstants";

export function prepareGuidewaySetResetTargets(guidewayKmat: Kmat, kmatIndex: number): ConfiguratorCsticTarget[] {
  const targets: ConfiguratorCsticTarget[] = [];

  for (let index = 1; index <= MAX_PIECE_AMOUNT_IN_KB; index++) {
    const key = GET_GUIDEWAY_PIECE_SIZE_CSTIC_NAME(index);
    if (isServerOwnedCstic(guidewayKmat, key)) {
      targets.push({ index: kmatIndex, type: guidewayKmat.type, key });
    }
  }

  [
    CSTIC_PART_SIZING_TYPE_NAME,
    CSTIC_PART_AMOUNT_NAME,
    CSTIC_AL_NAME,
    CSTIC_AR_NAME,
    CSTIC_ENABLE_ALAR_NAME,
    CSTIC_LENGTH_NAME,
  ]
    .filter((key) => isServerOwnedCstic(guidewayKmat, key))
    .forEach((key) => targets.push({ index: kmatIndex, type: guidewayKmat.type, key }));

  return targets;
}

export function prepareUnitGuidewayStepResetTargets(guideways: Kmat[]): ConfiguratorCsticTarget[] {
  return guideways.flatMap((kmat, index) => prepareGuidewaySetResetTargets(kmat, index));
}

export function prepareUnitCarriageStepResetTargets(carriages: Kmat[], mainKmats: Kmat[]): ConfiguratorCsticTarget[] {
  return [
    ...carriages
      .map((kmat, index) => ({ kmat, index }))
      .filter(({ kmat }) => isServerOwnedCstic(kmat, CSTIC_VSKITS_NAME))
      .map(({ kmat, index }) => ({
        index,
        type: kmat.type,
        key: CSTIC_VSKITS_NAME,
      })),
    ...rootTargetIfPresent(mainKmats, CSTIC_VHOE),
  ];
}

export function prepareClassicRailStepResetTargets(rails: Kmat[]): ConfiguratorCsticTarget[] {
  return [
    ...rails.flatMap((kmat, index) => prepareGuidewaySetResetTargets(kmat, index)),
    ...rootTargetIfPresent(rails, LOCATING_FACE_CODE_KEY),
  ];
}

export function prepareUnitCarriageResetTargets(
  carriage: Kmat,
  carriageIndex: number,
  mainKmats: Kmat[],
): ConfiguratorCsticTarget[] {
  return [
    ...(isServerOwnedCstic(carriage, CSTIC_VSKITS_NAME)
      ? [{ index: carriageIndex, type: carriage.type, key: CSTIC_VSKITS_NAME }]
      : []),
    ...rootTargetIfPresent(mainKmats, CSTIC_VHOE),
  ];
}

function rootTargetIfPresent(kmats: Kmat[], key: string): ConfiguratorCsticTarget[] {
  return kmats.some((kmat) => isServerOwnedCstic(kmat, key))
    ? [
        {
          type: KmatType.Main,
          key,
        },
      ]
    : [];
}

function isServerOwnedCstic(kmat: Kmat, key: string): boolean {
  const cstic = kmat.cstics[key];
  return Boolean(cstic && !cstic.fake && !cstic.readonly);
}
